/**
 * Client-side chart mutation engine
 *
 * Deterministically applies chart mutations to state.
 * Must match backend chart_mutation.py logic.
 */

import { emitToothEvent } from "../../../utils/toothEventBus";
import { eventBus } from "../events/eventBus";

export interface ChartMutationState {
  states?: Record<string, string>;
  surfaces?: Record<string, string[] | unknown>;
  flags?: Record<string, unknown>;
  movements?: Record<string, unknown>;
}

export interface ChartMutationFrame {
  id?: number;
  symbol?: string;
  tooth?: string;
  surface?: string | null;
  multi_tooth?: string | string[] | null;
  visit_id?: number;
  timestamp?: string;
  [key: string]: unknown;
}

export type ChartMutationStateInput = ChartMutationState;

export function applyChartMutationClient(
  state: ChartMutationState,
  frame: ChartMutationFrame
): void {
  const mtype = frame.symbol;
  const tooth = frame.tooth;

  if (!mtype || !tooth) return;

  if (!state.states) state.states = {};
  if (!state.surfaces) state.surfaces = {};
  if (!state.flags) state.flags = {};
  if (!state.movements) state.movements = {};
  const states = state.states;
  const surfaces = state.surfaces;

  const touchedTeeth: string[] = [];
  const prevStates: Record<string, string | null> = {};
  let effectiveType = mtype;
  if (mtype === "EXTRACT") effectiveType = "EXTRACTION";
  else if (mtype === "CROWN") effectiveType = "CROWN_PLACED";

  switch (effectiveType) {
    case "EXTRACTION":
      prevStates[tooth] = states[tooth] || "Normal";
      states[tooth] = "Missing";
      touchedTeeth.push(tooth);
      break;

    case "FILLING":
      prevStates[tooth] = states[tooth] || "Normal";
      states[tooth] = "Filling";
      touchedTeeth.push(tooth);
      if (frame.surface && surfaces) {
        const sur = surfaces as Record<string, string[]>;
        if (!sur[tooth]) sur[tooth] = [];
        if (!sur[tooth].includes(frame.surface)) {
          sur[tooth].push(frame.surface);
        }
      }
      break;

    case "CROWN_PLACED":
      prevStates[tooth] = states[tooth] || "Normal";
      states[tooth] = "Crown";
      touchedTeeth.push(tooth);
      break;

    case "RCT_OPEN":
      prevStates[tooth] = states[tooth] || "Normal";
      states[tooth] = "RCT_Open";
      touchedTeeth.push(tooth);
      break;

    case "RCT_DONE":
      prevStates[tooth] = states[tooth] || "Normal";
      states[tooth] = "RCT_Done";
      touchedTeeth.push(tooth);
      break;

    case "RCT_CROWN":
      prevStates[tooth] = states[tooth] || "Normal";
      states[tooth] = "RCT_Crown";
      touchedTeeth.push(tooth);
      break;

    case "RCT_CC":
      prevStates[tooth] = states[tooth] || "Normal";
      states[tooth] = "RCT_CC";
      touchedTeeth.push(tooth);
      break;

    case "CROWN_CUTTING":
      prevStates[tooth] = states[tooth] || "Normal";
      states[tooth] = "Crown_Cutting";
      touchedTeeth.push(tooth);
      break;

    case "BRIDGE_PLACED":
      if (frame.multi_tooth) {
        const teeth = typeof frame.multi_tooth === "string"
          ? frame.multi_tooth.split(",").map((t) => t.trim())
          : frame.multi_tooth;
        teeth.forEach((t) => {
          if (t) {
            prevStates[t] = states[t] || "Normal";
            states[t] = "Crown";
            touchedTeeth.push(t);
          }
        });
      }
      break;

    case "IMPLANT_PLACED":
      prevStates[tooth] = states[tooth] || "Normal";
      states[tooth] = "Implant";
      touchedTeeth.push(tooth);
      break;

    case "IMPLANT_RESTORED":
      prevStates[tooth] = states[tooth] || "Normal";
      states[tooth] = "Crown";
      touchedTeeth.push(tooth);
      break;

    case "REIMPLANTED":
      prevStates[tooth] = states[tooth] || "Normal";
      states[tooth] = "Normal";
      touchedTeeth.push(tooth);
      break;

    case "NONE":
      break;

    default:
      break;
  }

  if (frame.visit_id && tooth) {
    const nextStates: Record<string, string | null> = {};
    touchedTeeth.forEach((t) => {
      nextStates[t] = states[t] || "Normal";
    });
    eventBus.emit("TOOTH_EVENT", {
      type: "procedure",
      encounterId: frame.visit_id,
      tooth_code: tooth,
      payload: {
        symbol: frame.symbol,
        multi_tooth: frame.multi_tooth || null,
        prev_states: prevStates,
        next_states: nextStates,
        replay: true,
      },
    });
    emitToothEvent({
      id: (frame.id as number) || 0,
      encounter_id: frame.visit_id,
      tooth_code: tooth,
      surface: frame.surface || null,
      event_type: effectiveType.toLowerCase() || "procedure",
      payload: {
        symbol: frame.symbol,
        multi_tooth: frame.multi_tooth || null,
      },
      created_at: frame.timestamp || new Date().toISOString(),
    });
  }
}
